'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useSession, signOut } from 'next-auth/react'
import { FaUser, FaSignOutAlt, FaTimes, FaBars } from 'react-icons/fa'

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/#eventos', label: 'Eventos' },
  { href: '/#contato', label: 'Contato' },
]

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { data: session } = useSession()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Efeito para mudar o header ao rolar a página
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Bloquear scroll do body quando menu mobile aberto (evita arrastar página no iOS)
  useEffect(() => {
    if (!mobileOpen) return
    const scrollY = window.scrollY
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      window.scrollTo(0, scrollY)
    }
  }, [mobileOpen])

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-500 ease-in-out pt-safe ${
        isScrolled ? 'bg-black/90 backdrop-blur-md py-3 shadow-lg shadow-black/20' : 'bg-transparent py-6 md:py-8'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 flex justify-between items-center">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 relative group">
          <Image 
            src="/assets/images/logo.png" 
            alt="Vibe Logo" 
            width={140} 
            height={50} 
            className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105" 
            priority 
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-10">
          <div className="flex gap-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors uppercase tracking-wide relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#ffa300] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ))}
          </div>

          <div className="h-6 w-[1px] bg-white/20 mx-2"></div>

          <div className="flex items-center gap-4">
            {session ? (
              <>
                {(session.user.role === 'ADMIN' || session.user.role === 'VALIDATOR') && (
                  <Link
                    href={session.user.role === 'ADMIN' ? '/admin' : '/admin/validador'}
                    className="text-sm font-bold text-[#ffa300] hover:text-yellow-400 transition-colors uppercase tracking-wide"
                  >
                    {session.user.role === 'ADMIN' ? 'Admin' : 'Validador'}
                  </Link>
                )}
                
                <div className="relative group">
                  <Link
                    href="/profile"
                    className="flex items-center gap-3 text-white hover:text-[#ffa300] transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center group-hover:border-[#ffa300] transition-colors">
                       <FaUser className="text-sm" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-zinc-400 group-hover:text-zinc-300">Olá,</span>
                      <span className="text-sm font-bold leading-none max-w-[100px] truncate">{session.user.name?.split(' ')[0]}</span>
                    </div>
                  </Link>
                </div>

                <button
                  onClick={() => signOut()}
                  className="text-zinc-400 hover:text-red-400 transition-colors ml-2"
                  title="Sair"
                >
                  <FaSignOutAlt className="text-lg" />
                </button>
              </>
            ) : (
              <Link 
                href="/login" 
                className="px-6 py-2.5 bg-white text-black font-bold text-sm rounded-full hover:bg-[#ffa300] transition-all duration-300 shadow-[0_0_15px_rgba(255,255,255,0.2)] hover:shadow-[0_0_20px_rgba(255,163,0,0.4)]"
              >
                ENTRAR
              </Link>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white text-2xl focus:outline-none min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Navigation Overlay - portal no body + touch-action: none para evitar transparência ao arrastar */}
      {mounted &&
        typeof document !== 'undefined' &&
        document.body &&
        createPortal(
          <div
            className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 pt-safe pb-safe transition-all duration-300 touch-none select-none overscroll-none ${mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
            style={{ background: '#000', transform: 'translateZ(0)' }}
            onTouchMove={(e) => e.preventDefault()}
          >
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="text-2xl font-bold text-white hover:text-[#ffa300] transition-colors py-4 min-h-[48px] flex items-center"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {session ? (
              <div className="flex flex-col items-center gap-6 mt-8 border-t border-white/10 pt-8 w-full max-w-xs">
                <Link
                  href="/profile"
                  className="flex items-center gap-3 text-white hover:text-[#ffa300] transition-colors py-4 min-h-[48px]"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <FaUser className="text-lg" />
                  </div>
                  <span className="text-xl font-bold">Meu Perfil</span>
                </Link>

                {(session.user.role === 'ADMIN' || session.user.role === 'VALIDATOR') && (
                  <Link
                    href={session.user.role === 'ADMIN' ? '/admin' : '/admin/validador'}
                    className="text-lg font-bold text-[#ffa300] py-4 min-h-[48px] flex items-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    {session.user.role === 'ADMIN' ? 'Painel Admin' : 'Validador'}
                  </Link>
                )}

                <button
                  onClick={() => {
                    signOut()
                    setMobileOpen(false)
                  }}
                  className="text-red-400 font-semibold flex items-center gap-2 py-4 min-h-[48px]"
                >
                  <FaSignOutAlt /> Sair
                </button>
              </div>
            ) : (
              <Link
                href="/login"
                className="px-8 py-4 min-h-[48px] flex items-center justify-center bg-[#ffa300] text-black font-bold text-lg rounded-full hover:bg-white transition-all mt-4"
                onClick={() => setMobileOpen(false)}
              >
                Login / Cadastro
              </Link>
            )}
          </div>,
          document.body
        )}
    </header>
  )
}
